<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { onClickOutside } from '@vueuse/core'

import AppIcon from '@/components/ui/AppIcon.vue'
import LocaleFlag from '@/components/layout/LocaleFlag.vue'
import { LOCALE_LABELS, SELECTABLE_LOCALES, type SupportedLocale } from '@/lib/constants'
import { setLocale } from '@/lib/i18n'
import { useAuthStore } from '@/stores/auth'

withDefaults(
  defineProps<{
    tone?: 'default' | 'onDark'
  }>(),
  { tone: 'default' },
)

const { t, locale } = useI18n()
const auth = useAuthStore()

const open = ref(false)
const root = ref<HTMLElement | null>(null)
onClickOutside(root, () => (open.value = false))

const current = computed(() => locale.value as SupportedLocale)

const options = computed(() =>
  SELECTABLE_LOCALES.map((value) => ({ value, label: LOCALE_LABELS[value] })),
)

async function choose(next: SupportedLocale) {
  open.value = false
  if (next === current.value) return
  setLocale(next)

  if (auth.isAuthenticated) {
    try {
      await auth.updateProfile({ locale: next })
    } catch {
      // A failed persist is not worth interrupting the user; the UI language
      // has already changed locally.
    }
  }
}
</script>

<template>
  <div ref="root" class="relative">
    <button
      type="button"
      class="flex items-center gap-x-1.5 rounded-lg px-2 py-1.5"
      :class="tone === 'onDark' ? 'hover:bg-white/10' : 'hover:bg-gray-100 dark:hover:bg-neutral-700'"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :aria-label="t('common.language')"
      @click="open = !open"
    >
      <LocaleFlag :locale="current" />
      <AppIcon name="chevron-down" size="sm" class="text-gray-400" />
    </button>

    <div
      v-if="open"
      role="listbox"
      class="absolute end-0 z-40 mt-2 w-40 rounded-lg bg-white py-1 shadow-lg ring-1 ring-gray-200 dark:bg-neutral-800 dark:ring-neutral-700"
    >
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        role="option"
        class="flex w-full items-center gap-x-2 px-3 py-2 text-start text-sm text-gray-700 hover:bg-gray-50 dark:text-neutral-200 dark:hover:bg-neutral-700"
        :aria-selected="option.value === current"
        @click="choose(option.value)"
      >
        <LocaleFlag :locale="option.value" />
        {{ option.label }}
      </button>
    </div>
  </div>
</template>
